import { Fragment, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ExcelJS from "exceljs";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, Loader2, RefreshCw, FileText, CheckCircle2, Circle, History, Pencil, Trash2, ChevronDown, ChevronRight as ChevronRightIcon, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { RegistrationIssuesPanel } from "./RegistrationIssuesPanel";

const fitColumnWidths = (rows: Record<string, unknown>[]) => {
  const widths = new Map<string, number>();
  rows.forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      const next = Math.min(40, Math.max(key.length, String(value ?? "").length) + 2);
      widths.set(key, Math.max(widths.get(key) || 12, next));
    });
  });
  return widths;
};

const dataUrlToBuffer = (dataUrl: string) => {
  const [, base64] = dataUrl.split(",");
  const bytes = atob(base64 || "");
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
  return arr.buffer;
};

const imageFormatFromType = (contentType: string | null) => {
  if (!contentType) return "jpeg" as const;
  if (contentType.includes("png")) return "png" as const;
  return "jpeg" as const;
};

interface Registration {
  id: string;
  conference_slug: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  mobile_country_code: string;
  mobile_number: string;
  spouse_mobile_country_code?: string | null;
  spouse_mobile_number?: string | null;
  spouse_first_name?: string | null;
  spouse_middle_name?: string | null;
  spouse_last_name?: string | null;
  spouse_email?: string | null;
  boarding_type?: string | null;
  conference_fee?: number | null;
  state?: string | null;
  email: string;
  region: string;
  city: string;
  mailing_address: string;
  category: string | null;
  category_price: number | null;
  family_type?: string | null;
  amount_paid?: number | null;
  amount_due?: number | null;
  payment_choice: string | null;
  payment_status: string | null;
  children_0_5: number;
  children_6_12: number;
  teenagers_13_17: number;
  adults_18_plus: number;
  total_members: number;
  dietary_notes: string | null;
  zelle_memo: string;
  payment_screenshot_path: string | null;
  transaction_reference: string | null;
  admin_notes: string | null;
  is_student?: boolean | null;
  university?: string | null;
  needs_scholarship?: boolean | null;
  scholarship_requested_by?: string | null;
  general_comments?: string | null;
  created_at: string;
}

interface MemberRow {
  id: string;
  registration_id: string;
  first_name: string;
  middle_name: string | null;
  last_name: string;
  relationship: string;
  age_group: string;
  attendee_type?: string | null;
}

interface AuditRow {
  id: string;
  registration_id: string;
  from_status: string | null;
  to_status: string;
  transaction_reference: string | null;
  notes: string | null;
  changed_by_name: string | null;
  changed_by_email: string | null;
  changed_at: string;
}

interface ExportRow {
  "Registration ID": string;
  "First Name": string;
  "Middle Name": string;
  "Last Name": string;
  "Mobile": string;
  "Email": string;
  "Region": string;
  "City": string;
  "Mailing Address": string;
  "Category": string;
  "Is Student": string;
  "University": string;
  "Category Price (USD)": number | string;
  "Payment Choice": string;
  "Payment Status": string;
  "Transaction Reference": string;
  "Admin Notes": string;
  "Spouse Name": string;
  "Spouse Mobile": string;
  "Children Names (with age)": string;
  "Kids 0-5 Names": string;
  "Kids 6-12 Names": string;
  "Teens 13-17 Names": string;
  "Other Adult Names": string;
  "Kids (0-5)": number;
  "Kids (6-12)": number;
  "Teens (13-17)": number;
  "Adults (18+)": number;
  "Total Attendees": number;
  "Dietary / Medical Notes": string;
  "Zelle Memo": string;
  "Payment Screenshot": string;
  "Scholarship Assistance Requested": string;
  "Scholarship Assistance Requested By": string;
  "General Comments / Special Requests": string;
  "Submitted At": string;
  [key: string]: string | number | undefined;
}

interface ConfirmationExportRow {
  "Registration ID": string;
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Mobile": string;
  "Region": string;
  "City": string;
  "Category": string;
  "Payment Choice": string;
  "Payment Status": string;
  "Zelle Memo": string;
  Screenshot: string;
  "Submitted At": string;
}

type SortKey = keyof Registration;

interface Props {
  isAuthorized: boolean;
  userRole?: string;
  userRegionId?: string | null;
  selectedRegion?: string;
  onSelectedRegionChange?: (region: string) => void;
}

export const ConferenceRegistrationsManager = ({
  isAuthorized,
  userRole,
  userRegionId,
  selectedRegion,
  onSelectedRegionChange,
}: Props) => {
  const [rows, setRows] = useState<Registration[]>([]);
  const [members, setMembers] = useState<Record<string, MemberRow[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [regionName, setRegionName] = useState<string | null>(null);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterPayment, setFilterPayment] = useState<string>("all");
  const [filterMethod, setFilterMethod] = useState<string>("all");
  const [filterScholarship, setFilterScholarship] = useState<boolean>(false);

  // Sync filters from URL query params (deep-links from analytics KPI cards).
  const location = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const p = (sp.get("payment") || "").toLowerCase();
    if (p === "confirmed" || p === "pending" || p === "rejected") setFilterPayment(p);
    const c = (sp.get("category") || "").toLowerCase();
    if (c && ["individual", "student", "couple", "family"].includes(c)) setFilterCategory(c);
    const sch = sp.get("scholarship");
    if (sch === "1" || sch === "true") setFilterScholarship(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // Mark Paid/Unpaid dialog state
  const [editing, setEditing] = useState<Registration | null>(null);
  const [txnRef, setTxnRef] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Audit history dialog
  const [historyFor, setHistoryFor] = useState<Registration | null>(null);
  const [history, setHistory] = useState<AuditRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const isRegionalAdmin = userRole === "regional_admin";
  const canDelete = userRole === "global_admin" || userRole === "president";
  const [deleting, setDeleting] = useState<Registration | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      if (deleting.payment_screenshot_path) {
        await supabase.storage.from("conference-payments").remove([deleting.payment_screenshot_path]);
      }
      const { error } = await supabase
        .from("conference_registrations")
        .delete()
        .eq("id", deleting.id);
      if (error) throw error;
      setRows((prev) => prev.filter((r) => r.id !== deleting.id));
      toast({ title: "Registration deleted", description: `${deleting.first_name} ${deleting.last_name} removed.` });
      setDeleting(null);
    } catch (err: unknown) {
      toast({
        title: "Delete failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;
    (async () => {
      let region: string | null = null;
      if (isRegionalAdmin && userRegionId) {
        const { data } = await supabase
          .from("regions")
          .select("name")
          .eq("id", userRegionId)
          .single();
        region = (data as any)?.name ?? null;
        setRegionName(region);
      }
      await load(region);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, userRegionId, isRegionalAdmin]);

  const load = async (region?: string | null) => {
    setLoading(true);
    let query = supabase
      .from("conference_registrations" as any)
      .select("*")
      .order("created_at", { ascending: false });
    const scopeRegion = region ?? regionName;
    if (isRegionalAdmin && scopeRegion) {
      query = query.eq("region", scopeRegion);
    }
    const { data, error } = await query;
    if (error) {
      toast({ title: "Failed to load registrations", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }
    const regs = (data || []) as any as Registration[];
    setRows(regs);

    // Fetch members
    const ids = regs.map((r) => r.id);
    if (ids.length) {
      const { data: mdata } = await (supabase as any)
        .from("registration_members")
        .select("*")
        .in("registration_id", ids);
      const grouped: Record<string, MemberRow[]> = {};
      (mdata || []).forEach((m: MemberRow) => {
        (grouped[m.registration_id] ||= []).push(m);
      });
      setMembers(grouped);
    } else {
      setMembers({});
    }
    setLoading(false);
  };

  const [localRegionTab, setLocalRegionTab] = useState<string>("all");
  const regionTab = selectedRegion ?? localRegionTab;
  const setRegionTab = (region: string) => {
    setLocalRegionTab(region);
    onSelectedRegionChange?.(region);
  };

  const regionsAvailable = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.region) set.add(r.region); });
    return Array.from(set).sort();
  }, [rows]);

  const spouseNameFor = (regId: string) => {
    const list = members[regId] || [];
    const sp = list.find((m) => m.relationship === "spouse");
    if (!sp) return "";
    return [sp.first_name, sp.middle_name, sp.last_name].filter(Boolean).join(" ");
  };

  const isTestRow = (r: Registration): boolean => {
    const email = (r.email || "").toLowerCase().trim();
    const fn = (r.first_name || "").toLowerCase().trim();
    const ln = (r.last_name || "").toLowerCase().trim();
    const full = `${fn} ${ln}`.trim();
    if (!email) return true;
    if (/^test@|@example\.|@test\.|\+test@|noreply@|lovable\.dev/.test(email)) return true;
    if (fn === "test" || ln === "test" || fn.includes("test") && ln.includes("test")) return true;
    if (full.includes("test user") || full.includes("suresh test")) return true;
    return false;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = q
      ? rows.filter((r) => {
          const spouseName = spouseNameFor(r.id);
          const spouseMobile = r.spouse_mobile_number
            ? `${r.spouse_mobile_country_code || ""} ${r.spouse_mobile_number}`
            : "";
          return [
            r.first_name,
            r.middle_name,
            r.last_name,
            `${r.first_name} ${r.last_name}`,
            r.email,
            r.mobile_number,
            `${r.mobile_country_code || ""} ${r.mobile_number || ""}`,
            spouseName,
            spouseMobile,
            r.region,
            r.city,
            r.zelle_memo,
            r.transaction_reference,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
        })
      : [...rows];
    out = out.filter(r => !isTestRow(r));
    if (!isRegionalAdmin && regionTab !== "all") {
      out = out.filter(r => r.region === regionTab);
    }
    if (filterCategory !== "all") {
      out = out.filter(r => (r.category || "").toLowerCase() === filterCategory);
    }
    if (filterPayment !== "all") {
      out = out.filter(r => (r.payment_status || "").toLowerCase() === filterPayment);
    }
    if (filterMethod !== "all") {
      out = out.filter(r => (r.payment_choice || "").toLowerCase() === filterMethod);
    }
    if (filterScholarship) {
      out = out.filter(r => !!r.needs_scholarship);
    }
    out.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return out;
  }, [rows, search, sortKey, sortDir, regionTab, isRegionalAdmin, members, filterCategory, filterPayment, filterMethod, filterScholarship]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  useEffect(() => {
    setPage(1);
  }, [search, regionTab, filterCategory, filterPayment, filterMethod, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const categoriesAvailable = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.category) set.add(r.category.toLowerCase()); });
    return Array.from(set).sort();
  }, [rows]);

  const methodsAvailable = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.payment_choice) set.add(r.payment_choice.toLowerCase()); });
    return Array.from(set).sort();
  }, [rows]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  const signedUrl = async (path: string | null) => {
    if (!path) return null;
    const { data } = await supabase.storage.from("conference-payments").createSignedUrl(path, 60 * 10);
    return data?.signedUrl || null;
  };

  const openScreenshot = async (path: string | null) => {
    const url = await signedUrl(path);
    if (url) window.open(url, "_blank", "noopener");
    else toast({ title: "No screenshot available", variant: "destructive" });
  };

  const openEdit = (r: Registration) => {
    setEditing(r);
    setTxnRef(r.transaction_reference || "");
    setAdminNote(r.admin_notes || "");
  };

  const savePayment = async (nextStatus: "confirmed" | "pending") => {
    if (!editing) return;
    setSaving(true);
    const fromStatus = editing.payment_status || "pending";

    // Resolve current admin (best-effort) for audit metadata
    let adminId: string | null = null;
    let adminName: string | null = null;
    let adminEmail: string | null = null;
    try {
      const { data: u } = await supabase.auth.getUser();
      adminEmail = u?.user?.email ?? null;
      const { data: au } = await (supabase as any)
        .from("admin_users")
        .select("id, full_name, email")
        .eq("auth_user_id", u?.user?.id)
        .maybeSingle();
      if (au) {
        adminId = au.id;
        adminName = au.full_name || null;
        adminEmail = au.email || adminEmail;
      }
    } catch {}

    const { error } = await (supabase as any)
      .from("conference_registrations")
      .update({
        payment_status: nextStatus,
        transaction_reference: txnRef.trim() || null,
        admin_notes: adminNote.trim() || null,
      })
      .eq("id", editing.id);

    if (error) {
      setSaving(false);
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }

    // Insert audit row (best-effort)
    await (supabase as any).from("payment_status_audit").insert({
      registration_id: editing.id,
      from_status: fromStatus,
      to_status: nextStatus,
      transaction_reference: txnRef.trim() || null,
      notes: adminNote.trim() || null,
      changed_by_admin_id: adminId,
      changed_by_name: adminName,
      changed_by_email: adminEmail,
    });

    setRows((prev) =>
      prev.map((row) =>
        row.id === editing.id
          ? {
              ...row,
              payment_status: nextStatus,
              transaction_reference: txnRef.trim() || null,
              admin_notes: adminNote.trim() || null,
            }
          : row
      )
    );

    // Send confirmation email when marking as paid (best-effort)
    if (nextStatus === "confirmed" && editing.email) {
      try {
        await supabase.functions.invoke("send-payment-received-confirmation", {
          body: {
            email: editing.email,
            firstName: editing.first_name,
            lastName: editing.last_name,
            region: (editing as any).region ?? null,
            city: (editing as any).city ?? null,
            totalMembers: (editing as any).total_members ?? null,
            amountPaid: (editing as any).amount_paid ?? (editing as any).category_price ?? null,
            transactionReference: txnRef.trim() || null,
          },
        });
      } catch (e) {
        console.error("send-payment-received-confirmation invoke failed", e);
      }
    }

    // Push updated status to the region's Google Sheet (best-effort, non-blocking)
    try {
      const region = (editing as any).region;
      if (region) {
        supabase.functions
          .invoke("live-sync-registrations", { body: { region } })
          .catch((e) => console.warn("live-sync-registrations failed", e));
      }
    } catch (e) {
      console.warn("live-sync trigger failed", e);
    }

    setSaving(false);
    setEditing(null);
    toast({
      title: nextStatus === "confirmed" ? "Marked as paid — email sent" : "Marked unpaid",
      description: `${editing.first_name} ${editing.last_name}`,
    });
  };

  const openHistory = async (r: Registration) => {
    setHistoryFor(r);
    setHistoryLoading(true);
    setHistory([]);
    const { data, error } = await (supabase as any)
      .from("payment_status_audit")
      .select("*")
      .eq("registration_id", r.id)
      .order("changed_at", { ascending: false });
    if (error) {
      toast({ title: "Failed to load history", description: error.message, variant: "destructive" });
    } else {
      setHistory((data || []) as AuditRow[]);
    }
    setHistoryLoading(false);
  };

  const ageLabel = (g?: string | null) => {
    switch (g) {
      case "adult": return "18+";
      case "teen": return "13-17";
      case "child_6_12": return "6-12";
      case "child_0_5": return "0-5";
      default: return g || "";
    }
  };
  const formatMember = (m: MemberRow) => {
    const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
    const ag = ageLabel(m.age_group);
    return ag ? `${name} (${ag})` : name;
  };

  const familyNamesByRelationship = (regId: string) => {
    const list = members[regId] || [];
    // 18+ dependents (relationship=child but age_group=adult, or child_category=adult_18_plus)
    // are reported as Additional Adults, not as children.
    const isAdultDependent = (m: MemberRow) =>
      m.relationship === "child" && ((m as any).child_category === "adult_18_plus" || m.age_group === "adult");
    const spouse = list.filter((m) => m.relationship === "spouse").map(formatMember).join("; ");
    const children = list.filter((m) => m.relationship === "child" && !isAdultDependent(m)).map(formatMember).join("; ");
    const otherAdults = list
      .filter((m) => m.relationship === "other_adult" || isAdultDependent(m))
      .map(formatMember)
      .join("; ");
    // Group children by age band so the Excel has explicit columns per band
    const byBand = (band: string) =>
      list
        .filter((m) => m.relationship === "child" && !isAdultDependent(m) && ageLabel(m.age_group) === band)
        .map((m) => [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" "))
        .join("; ");
    return {
      spouse,
      children,
      otherAdults,
      children_0_5_names: byBand("0-5"),
      children_6_12_names: byBand("6-12"),
      teens_names: byBand("13-17"),
    };
  };


  const yearFromSlug = (slug?: string | null) => {
    const m = (slug || "").match(/(20\d{2})/);
    return m ? m[1] : "";
  };

  // Mirror the server-side test/dedup filters used by live-sync-registrations
  // so downloaded Excel never contains junk or duplicate rows.
  const isTestEntry = (r: Registration): boolean => {
    const email = (r.email || "").toLowerCase().trim();
    const fn = (r.first_name || "").toLowerCase().trim();
    const ln = (r.last_name || "").toLowerCase().trim();
    const full = `${fn} ${ln}`.trim();
    if (!email) return true;
    if (/^test@|@example\.|@test\.|\+test@|noreply@|lovable\.dev/.test(email)) return true;
    if (fn === "test" || ln === "test" || full === "test user" || full === "test test") return true;
    return false;
  };

  const dedupeRegistrations = (list: Registration[]): Registration[] => {
    const rank: Record<string, number> = { confirmed: 3, paid: 3, pending: 2, rejected: 1 };
    const best = new Map<string, Registration>();
    for (const r of list) {
      if (isTestEntry(r)) continue;
      const key = `${r.conference_slug || ""}|${(r.email || "").toLowerCase().trim()}`;
      const prev = best.get(key);
      if (!prev) { best.set(key, r); continue; }
      const rNew = rank[r.payment_status || ""] ?? 0;
      const rOld = rank[prev.payment_status || ""] ?? 0;
      if (rNew > rOld) best.set(key, r);
      else if (rNew === rOld && new Date(r.created_at) > new Date(prev.created_at)) best.set(key, r);
    }
    return Array.from(best.values());
  };

  const downloadExcel = async () => {
    if (!isAuthorized) return;
    toast({ title: "Preparing Excel file…" });

    const exportRows = dedupeRegistrations(filtered);
    const removed = filtered.length - exportRows.length;
    if (removed > 0) {
      toast({ title: `Filtered ${removed} test/duplicate row${removed === 1 ? "" : "s"} from export` });
    }

    // Demographic rows (Section 1 per spec)
    const demoRows = exportRows.map((r) => {

      const fam = familyNamesByRelationship(r.id);
      const spouseName = spouseNameFor(r.id);
      const primaryName = [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ");
      const childrenDetails = fam.children || "";
      return {
        "Registration ID": r.id,
        "Registration Date": new Date(r.created_at).toISOString(),
        "Conference Year": yearFromSlug(r.conference_slug),
        "Region": r.region,
        "Family Name": r.last_name,
        "Primary Contact Name": primaryName,
        "Primary Contact Mobile Number": r.mobile_number ? `${r.mobile_country_code || ""} ${r.mobile_number}`.trim() : "",
        "Spouse Name": spouseName || [r.spouse_first_name, r.spouse_middle_name, r.spouse_last_name].filter(Boolean).join(" "),
        "Spouse Mobile Number": r.spouse_mobile_number ? `${r.spouse_mobile_country_code || ""} ${r.spouse_mobile_number}`.trim() : "",
        "Spouse Email": r.spouse_email || "",
        "Email Address": r.email,
        "Address": r.mailing_address,
        "City": r.city,
        "State": r.state || "",
        "Registration Category": r.category || "",
        "Family Type": r.family_type || "",
        "Individual Attendees (Type)": r.category === "individual"
          ? (members[r.id] || [])
              .filter((m) => m.relationship === "primary" || m.relationship === "other_adult")
              .map((m) => {
                const name = [m.first_name, m.middle_name, m.last_name].filter(Boolean).join(" ");
                const t = ((m as any).attendee_type || "adult").toString();
                return `${name} (${t === "student" ? "Student" : "Adult"})`;
              })
              .join("; ")
          : "",
        "Calculated Registration Fee": r.category_price ?? 0,
        "Amount Paid": r.amount_paid ?? 0,
        "Amount Due": r.amount_due ?? Math.max(Number(r.category_price ?? 0) - Number(r.amount_paid ?? 0), 0),
        "Payment Option": r.payment_choice || "",
        "Payment Status": r.payment_status === "confirmed" ? "Paid" : "Not Paid",
        "Boarding Type": r.boarding_type === "hotel" ? "Hotel Accommodation" : r.boarding_type === "camp" ? "Camp Accommodation" : "",
        "Hotel Accommodation Selected": r.boarding_type === "hotel" ? "Yes" : r.boarding_type === "camp" ? "No" : "",
        "Conference Registration & Food Fee": r.conference_fee ?? "",
        "Adults Count": r.adults_18_plus,
        "Teens Count (13-17)": r.teenagers_13_17,
        "Children Count (6-12)": r.children_6_12,
        "Children Count (0-5)": r.children_0_5,
        "Total Attendees": r.total_members,
        "Children / Kids Details": childrenDetails,
        "Scholarship Assistance Requested": r.needs_scholarship ? "Yes" : "No",
        "Scholarship Assistance Requested By": r.scholarship_requested_by || "",
        "General Comments / Special Requests": r.general_comments || "",
      };
    });

    const withUrls: ExportRow[] = await Promise.all(
      exportRows.map(async (r) => {
        const fam = familyNamesByRelationship(r.id);
        const screenshotUrl = await signedUrl(r.payment_screenshot_path);
        return {
          "Registration ID": r.id,
          "First Name": r.first_name,
          "Middle Name": r.middle_name || "",
          "Last Name": r.last_name,
          "Mobile": `${r.mobile_country_code} ${r.mobile_number}`,
          "Email": r.email,
          "Region": r.region,
          "City": r.city,
          "Mailing Address": r.mailing_address,
          "Category": r.category || "",
          "Family Type": r.family_type || "",
          "Is Student": r.is_student ? "Yes" : "No",
          "University": r.university || "",
          "Category Price (USD)": r.category_price ?? "",
          "Calculated Registration Fee": r.category_price ?? 0,
          "Amount Paid": r.amount_paid ?? 0,
          "Amount Due": r.amount_due ?? Math.max(Number(r.category_price ?? 0) - Number(r.amount_paid ?? 0), 0),
          "Payment Choice": r.payment_choice || "",
          "Payment Option": r.payment_choice || "",
          "Payment Status": r.payment_status === "confirmed" ? "Paid" : "Not Paid",
          "Transaction Reference": r.transaction_reference || "",
          "Admin Notes": r.admin_notes || "",
          "Spouse Name": fam.spouse || [r.spouse_first_name, r.spouse_middle_name, r.spouse_last_name].filter(Boolean).join(" "),
          "Spouse Mobile": r.spouse_mobile_number ? `${r.spouse_mobile_country_code || ""} ${r.spouse_mobile_number}`.trim() : "",
          "Spouse Email": r.spouse_email || "",
          "Boarding Type": r.boarding_type === "hotel" ? "Hotel Accommodation" : r.boarding_type === "camp" ? "Camp Accommodation" : "",
          "Hotel Accommodation Selected": r.boarding_type === "hotel" ? "Yes" : r.boarding_type === "camp" ? "No" : "",
          "Conference Registration & Food Fee": r.conference_fee ?? "",
          "Children Names (with age)": fam.children,
          "Kids 0-5 Names": fam.children_0_5_names,
          "Kids 6-12 Names": fam.children_6_12_names,
          "Teens 13-17 Names": fam.teens_names,
          "Other Adult Names": fam.otherAdults,
          "Kids (0-5)": r.children_0_5,
          "Kids (6-12)": r.children_6_12,
          "Teens (13-17)": r.teenagers_13_17,
          "Adults (18+)": r.adults_18_plus,
          "Total Attendees": r.total_members,
          "Dietary / Medical Notes": r.dietary_notes || "",
          "Zelle Memo": r.zelle_memo,
          "Payment Screenshot": screenshotUrl || r.payment_screenshot_path || "",
          "Scholarship Assistance Requested": r.needs_scholarship ? "Yes" : "No",
          "Scholarship Assistance Requested By": r.scholarship_requested_by || "",
          "General Comments / Special Requests": r.general_comments || "",
          "Submitted At": new Date(r.created_at).toISOString(),
        };
      })
    );

    // Build category summary
    const norm = (c: string | null) => (c || "Unspecified").toString();
    const summaryMap = new Map<string, { regs: number; kids_0_5: number; kids_6_12: number; teens: number; adults: number; total: number }>();
    for (const r of exportRows) {
      const k = norm(r.category);
      const cur = summaryMap.get(k) || { regs: 0, kids_0_5: 0, kids_6_12: 0, teens: 0, adults: 0, total: 0 };
      cur.regs += 1;
      cur.kids_0_5 += r.children_0_5 || 0;
      cur.kids_6_12 += r.children_6_12 || 0;
      cur.teens += r.teenagers_13_17 || 0;
      cur.adults += r.adults_18_plus || 0;
      cur.total += r.total_members || 0;
      summaryMap.set(k, cur);
    }
    const summary = Array.from(summaryMap.entries()).map(([Category, v]) => ({
      Category,
      Registrations: v.regs,
      "Kids (0-5)": v.kids_0_5,
      "Kids (6-12)": v.kids_6_12,
      "Teens (13-17)": v.teens,
      "Adults / Individuals (18+)": v.adults,
      "Total Attendees": v.total,
    }));
    const totals = summary.reduce(
      (acc, s) => ({
        Registrations: acc.Registrations + s.Registrations,
        "Kids (0-5)": acc["Kids (0-5)"] + s["Kids (0-5)"],
        "Kids (6-12)": acc["Kids (6-12)"] + s["Kids (6-12)"],
        "Teens (13-17)": acc["Teens (13-17)"] + s["Teens (13-17)"],
        "Adults / Individuals (18+)": acc["Adults / Individuals (18+)"] + s["Adults / Individuals (18+)"],
        "Total Attendees": acc["Total Attendees"] + s["Total Attendees"],
      }),
      { Registrations: 0, "Kids (0-5)": 0, "Kids (6-12)": 0, "Teens (13-17)": 0, "Adults / Individuals (18+)": 0, "Total Attendees": 0 }
    );
    summary.push({ Category: "TOTAL", ...totals });

    const workbook = new ExcelJS.Workbook();

    // === Section 1: Demographic Information ===
    const demoSheet = workbook.addWorksheet("Demographics");
    const demoHeaders = Object.keys(demoRows[0] || {
      "Registration ID": "", "Registration Date": "", "Conference Year": "", "Region": "",
      "Family Name": "", "Primary Contact Name": "", "Primary Contact Mobile Number": "",
      "Spouse Name": "", "Spouse Mobile Number": "", "Email Address": "", "Address": "",
      "City": "", "State": "", "Registration Category": "",
      "Adults Count": 0, "Teens Count (13-17)": 0, "Children Count (6-12)": 0, "Children Count (0-5)": 0,
      "Total Attendees": 0, "Children / Kids Details": "",
    });
    demoSheet.columns = demoHeaders.map((header) => ({
      header, key: header, width: fitColumnWidths(demoRows).get(header) || 18,
    }));
    demoRows.forEach((row) => demoSheet.addRow(row));
    demoSheet.getRow(1).font = { bold: true };
    demoSheet.views = [{ state: "frozen", ySplit: 1 }];

    const registrationsSheet = workbook.addWorksheet("Registrations");
    const registrationHeaders = Object.keys(withUrls[0] || {});
    registrationsSheet.columns = registrationHeaders.map((header) => ({ header, key: header, width: header === "Payment Screenshot" ? 20 : fitColumnWidths(withUrls).get(header) || 18 }));
    withUrls.forEach((row) => {
      const excelRow = registrationsSheet.addRow({
        ...row,
        "Payment Screenshot": row["Payment Screenshot"] ? "Screenshot attached" : "",
      });
      if (row["Payment Screenshot"]) {
        excelRow.getCell("Payment Screenshot").value = {
          text: "Screenshot attached",
          hyperlink: String(row["Payment Screenshot"]),
        };
      }
    });
    registrationsSheet.getRow(1).font = { bold: true };
    registrationsSheet.views = [{ state: "frozen", ySplit: 1 }];

    const summarySheet = workbook.addWorksheet("Category Summary");
    const summaryHeaders = Object.keys(summary[0] || {});
    summarySheet.columns = summaryHeaders.map((header) => ({ header, key: header, width: fitColumnWidths(summary).get(header) || 18 }));
    summary.forEach((row) => summarySheet.addRow(row));
    summarySheet.getRow(1).font = { bold: true };
    summarySheet.views = [{ state: "frozen", ySplit: 1 }];

    const confirmations: ConfirmationExportRow[] = withUrls
      .filter((r) => r["Payment Screenshot"])
      .map((r) => ({
        "Registration ID": r["Registration ID"],
        "First Name": r["First Name"],
        "Last Name": r["Last Name"],
        "Email": r["Email"],
        "Mobile": r["Mobile"],
        "Region": r["Region"],
        "City": r["City"],
        "Category": r["Category"],
        "Payment Choice": r["Payment Choice"],
        "Payment Status": r["Payment Status"],
        "Zelle Memo": r["Zelle Memo"],
        "Screenshot": r["Payment Screenshot"],
        "Submitted At": r["Submitted At"],
      }));
    if (confirmations.length) {
      const confirmationsSheet = workbook.addWorksheet("Payment Confirmations");
      const confirmationHeaders = [
        "Registration ID",
        "First Name",
        "Last Name",
        "Email",
        "Mobile",
        "Region",
        "City",
        "Category",
        "Payment Choice",
        "Payment Status",
        "Zelle Memo",
        "Screenshot",
        "Submitted At",
      ];
      confirmationsSheet.columns = confirmationHeaders.map((header) => ({
        header,
        key: header,
        width: header === "Screenshot" ? 28 : fitColumnWidths(confirmations).get(header) || 18,
      }));
      confirmationsSheet.getRow(1).font = { bold: true };
      confirmationsSheet.views = [{ state: "frozen", ySplit: 1 }];

      for (const row of confirmations) {
        const rowValues = { ...row, Screenshot: row.Screenshot ? "Screenshot attached" : "" };
        const excelRow = confirmationsSheet.addRow(rowValues);
        excelRow.height = row.Screenshot ? 120 : undefined;
        const screenshotCell = excelRow.getCell("Screenshot");
        if (row.Screenshot) {
          screenshotCell.value = { text: "Open screenshot", hyperlink: row.Screenshot };
          try {
            const response = await fetch(row.Screenshot);
            if (response.ok) {
              const blob = await response.blob();
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ""));
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(blob);
              });
              const imageId = workbook.addImage({
                buffer: dataUrlToBuffer(dataUrl),
                extension: imageFormatFromType(blob.type),
              });
              confirmationsSheet.addImage(imageId, {
                tl: { col: confirmationHeaders.indexOf("Screenshot") + 0.1, row: excelRow.number - 1 + 0.12 },
                ext: { width: 140, height: 100 },
                hyperlinks: { hyperlink: row.Screenshot, tooltip: "Open payment screenshot" },
              });
            }
          } catch (error) {
            console.warn("Unable to embed payment screenshot in Excel", error);
          }
        }
      }
    }

    const regionSuffix = isRegionalAdmin && regionName ? `-${regionName.toLowerCase().replace(/\s+/g, "-")}` : "";
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = `ofuesi-regional-conference-2026-registrations${regionSuffix}-${new Date().toISOString().slice(0,10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(href);
  };

  if (!isAuthorized) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          You don't have permission to view conference registrations.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle>Conference Registrations</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            OFUESI Regional Conference 2026{isRegionalAdmin && regionName ? ` — ${regionName} Region` : ""} — {rows.length} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search name, spouse, email, mobile, region, txn ref…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
          />
          <Button variant="outline" onClick={() => load()} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button onClick={downloadExcel} disabled={!filtered.length}>
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!isRegionalAdmin && regionsAvailable.length > 0 && (
          <Tabs value={regionTab} onValueChange={setRegionTab} className="mb-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="all" className="flex items-center gap-2">
                All Regions <Badge variant="secondary" className="text-[10px]">{rows.length}</Badge>
              </TabsTrigger>
              {regionsAvailable.map(rn => {
                const count = rows.filter(r => r.region === rn).length;
                return (
                  <TabsTrigger key={rn} value={rn} className="flex items-center gap-2">
                    {rn} <Badge variant="secondary" className="text-[10px]">{count}</Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        )}
        <RegistrationIssuesPanel
          rows={(isRegionalAdmin ? rows : (regionTab === "all" ? rows : rows.filter(r => r.region === regionTab))) as any}
          members={members as any}
          onRefresh={() => load()}
          onOpenRegistration={(id) => { setExpanded((prev) => { const n = new Set(prev); n.add(id); return n; }); const el = document.getElementById(`reg-row-${id}`); el?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
        />
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">No registrations found.</div>
        ) : (
          <div className="space-y-3">
            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Filter:</span>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categoriesAvailable.map(c => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterPayment} onValueChange={setFilterPayment}>
                <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="Payment" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="confirmed">Paid</SelectItem>
                  <SelectItem value="pending">Not Paid</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterMethod} onValueChange={setFilterMethod}>
                <SelectTrigger className="h-8 w-[160px]"><SelectValue placeholder="Method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {methodsAvailable.map(m => (
                    <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                variant={filterPayment === "confirmed" ? "default" : "outline"}
                className="h-8 text-xs"
                onClick={() => setFilterPayment(p => p === "confirmed" ? "all" : "confirmed")}
              >
                {filterPayment === "confirmed" ? "Paid: On" : "Paid only"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={filterPayment === "pending" ? "default" : "outline"}
                className="h-8 text-xs"
                onClick={() => setFilterPayment(p => p === "pending" ? "all" : "pending")}
              >
                {filterPayment === "pending" ? "Not Paid: On" : "Not Paid only"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant={filterScholarship ? "default" : "outline"}
                className="h-8 text-xs"
                onClick={() => setFilterScholarship(v => !v)}
              >
                {filterScholarship ? "Scholarship: On" : "Scholarship only"}
              </Button>
              {(filterCategory !== "all" || filterPayment !== "all" || filterMethod !== "all" || filterScholarship || search) && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setFilterCategory("all");
                    setFilterPayment("all");
                    setFilterMethod("all");
                    setFilterScholarship(false);
                    setSearch("");
                    setPage(1);
                  }}
                >
                  Clear all filters
                </Button>
              )}
              <div className="ml-auto text-muted-foreground">
                Showing <strong>{paged.length}</strong> of <strong>{filtered.length}</strong> {filtered.length === 1 ? "registration" : "registrations"}
              </div>
            </div>

            {/* Data grid with sticky header + frozen columns */}
            <div className="relative border rounded-md overflow-auto max-h-[70vh] pb-2">
              <table className="caption-bottom text-sm border-collapse" style={{ minWidth: 2000 }}>
                <thead className="sticky top-0 z-30 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
                  <tr className="border-b">
                    <th style={{ width: 40, minWidth: 40, maxWidth: 40, left: 0 }} className="sticky top-0 bg-background z-40 h-10 px-2 text-left" />
                    <th style={{ width: 56, minWidth: 56, maxWidth: 56, left: 40 }} className="sticky top-0 bg-background z-40 h-10 px-2 text-left font-medium text-muted-foreground border-r">S.No.</th>
                    <th style={{ width: 120, minWidth: 120, maxWidth: 120, left: 96 }} className="sticky top-0 bg-background z-40 h-10 px-2 text-left font-medium text-muted-foreground border-r">Reg ID</th>
                    <th style={{ width: 200, minWidth: 200, maxWidth: 200, left: 216 }} className="sticky top-0 bg-background z-40 h-10 px-3 text-left font-medium text-muted-foreground border-r cursor-pointer select-none shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)]" onClick={() => toggleSort("last_name")}>
                      Name{sortKey === "last_name" ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                    </th>
                    {([
                      ["created_at", "Submitted"],
                      ["category", "Category"] as [SortKey, string],
                      ["email", "Email"],
                      ["region", "Region"],
                      ["city", "City"],
                      ["total_members", "Attendees"],
                      ["payment_status", "Status"] as [SortKey, string],
                    ] as [SortKey, string][]).map(([k, label]) => {
                      const w = k === "created_at" ? 180 : k === "email" ? 200 : k === "city" ? 120 : k === "region" ? 100 : k === "category" ? 100 : k === "total_members" ? 90 : k === "payment_status" ? 110 : 120;
                      return (
                        <th key={k} style={{ minWidth: w }} className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => toggleSort(k)}>
                          {label}{sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
                        </th>
                      );
                    })}
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap">Mobile</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap">Spouse</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap">Family Type</th>
                    <th className="h-10 px-3 text-center font-medium text-muted-foreground">A18+</th>
                    <th className="h-10 px-3 text-center font-medium text-muted-foreground">T13–17</th>
                    <th className="h-10 px-3 text-center font-medium text-muted-foreground">K6–12</th>
                    <th className="h-10 px-3 text-center font-medium text-muted-foreground">K0–5</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground">Fee</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground">Paid</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground">Due</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap">Method</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground whitespace-nowrap">Txn Ref</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground">Memo</th>
                    <th className="h-10 px-3 text-left font-medium text-muted-foreground">Screenshot</th>
                    <th style={{ minWidth: 220 }} className="h-10 px-3 text-right font-medium text-muted-foreground sticky top-0 right-0 bg-background z-40 border-l shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((r, i) => {
                    const isPaid = (r.payment_status || "").toLowerCase() === "confirmed";
                    const isOpen = expanded.has(r.id);
                    const methodLabel: Record<string, string> = {
                      zelle: "Zelle",
                      online: "Online / PayPal",
                      in_person: "Cheque / In-person",
                      pay_later: "Pay Later",
                      pay_now: "Zelle (legacy)",
                      free: "Free (Student)",
                    };
                    const serial = (page - 1) * pageSize + i + 1;
                    const memberList = members[r.id] || [];
                    return (
                      <Fragment key={r.id}>
                        <tr className="group border-b hover:bg-muted/40 transition-colors">

                          <td style={{ width: 40, minWidth: 40, maxWidth: 40, left: 0 }} className="sticky bg-background group-hover:bg-muted/40 z-20 p-1 align-top">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleExpanded(r.id)} aria-label={isOpen ? "Collapse row" : "Expand row"}>
                              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                            </Button>
                          </td>
                          <td style={{ width: 56, minWidth: 56, maxWidth: 56, left: 40 }} className="sticky bg-background group-hover:bg-muted/40 z-20 px-2 py-2 text-xs text-muted-foreground border-r align-top">{serial}</td>
                          <td style={{ width: 120, minWidth: 120, maxWidth: 120, left: 96 }} className="sticky bg-background group-hover:bg-muted/40 z-20 px-2 py-2 text-[10px] font-mono text-muted-foreground border-r align-top truncate" title={r.id}>{r.id.slice(0, 8)}</td>
                          <td style={{ width: 200, minWidth: 200, maxWidth: 200, left: 216 }} className="sticky bg-background group-hover:bg-muted/40 z-20 px-3 py-2 font-medium border-r align-top shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)]">
                            <div className="truncate max-w-[190px]" title={`${r.first_name} ${r.last_name}`}>{r.first_name} {r.last_name}</div>
                            {r.needs_scholarship && (
                              <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-300 w-fit" title={r.scholarship_requested_by || "Scholarship review needed"}>⚑ Scholarship</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap align-top">{new Date(r.created_at).toLocaleString()}</td>
                          <td className="px-3 py-2 text-xs capitalize align-top">{r.category || "—"}</td>
                          <td className="px-3 py-2 text-xs align-top">{r.email}</td>
                          <td className="px-3 py-2 text-xs align-top">{r.region}</td>
                          <td className="px-3 py-2 text-xs align-top">{r.city}</td>
                          <td className="px-3 py-2 text-xs text-center align-top">{r.total_members}</td>
                          <td className="px-3 py-2 align-top">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                              {isPaid ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                              {isPaid ? "Paid" : "Not Paid"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs font-mono whitespace-nowrap align-top">{r.mobile_number ? `${r.mobile_country_code || ""} ${r.mobile_number}` : "—"}</td>
                          <td className="px-3 py-2 text-xs align-top">{spouseNameFor(r.id) || "—"}</td>
                          <td className="px-3 py-2 text-xs align-top">{(r.family_type || "—").replace(/_/g, " ")}</td>
                          <td className="px-3 py-2 text-xs text-center align-top">{r.adults_18_plus ?? 0}</td>
                          <td className="px-3 py-2 text-xs text-center align-top">{r.teenagers_13_17 ?? 0}</td>
                          <td className="px-3 py-2 text-xs text-center align-top">{r.children_6_12 ?? 0}</td>
                          <td className="px-3 py-2 text-xs text-center align-top">{r.children_0_5 ?? 0}</td>
                          <td className="px-3 py-2 text-xs font-mono align-top">${Number(r.category_price ?? 0).toFixed(0)}</td>
                          <td className="px-3 py-2 text-xs font-mono align-top">${Number(r.amount_paid ?? 0).toFixed(0)}</td>
                          <td className="px-3 py-2 text-xs font-mono align-top">${Number(r.amount_due ?? Math.max(Number(r.category_price ?? 0) - Number(r.amount_paid ?? 0), 0)).toFixed(0)}</td>
                          <td className="px-3 py-2 text-xs align-top">{methodLabel[r.payment_choice || ""] || r.payment_choice || "—"}</td>
                          <td className="px-3 py-2 text-xs font-mono max-w-[140px] truncate align-top" title={r.transaction_reference || ""}>{r.transaction_reference || "—"}</td>
                          <td className="px-3 py-2 text-xs font-mono max-w-[160px] truncate align-top" title={r.zelle_memo}>{r.zelle_memo}</td>
                          <td className="px-3 py-2 align-top">
                            {r.payment_screenshot_path ? (
                              <Button size="sm" variant="ghost" onClick={() => openScreenshot(r.payment_screenshot_path)}>
                                <FileText className="w-4 h-4 mr-1" /> View
                              </Button>
                            ) : (<span className="text-xs text-muted-foreground">—</span>)}
                          </td>
                          <td className="px-3 py-2 text-right sticky right-0 bg-background group-hover:bg-muted/40 z-20 border-l align-top shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)]">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openHistory(r)} title="View payment history">
                                <History className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant={isPaid ? "outline" : "default"} onClick={() => openEdit(r)}>
                                <Pencil className="w-3 h-3 mr-1" />
                                {isPaid ? "Edit" : "Mark Paid"}
                              </Button>
                              {canDelete && (
                                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleting(r)} title="Delete registration">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-muted/30 border-b">
                            <td colSpan={26} className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                                <div>
                                  <div className="font-semibold text-sm mb-1">Primary Registrant</div>
                                  <div>{r.first_name} {r.middle_name || ""} {r.last_name}</div>
                                  <div className="text-muted-foreground">{r.email}</div>
                                  <div className="text-muted-foreground">{r.mobile_country_code} {r.mobile_number}</div>
                                  <div className="text-muted-foreground">{[r.city, r.state, r.region].filter(Boolean).join(", ")}</div>
                                  {r.mailing_address && <div className="text-muted-foreground mt-1">{r.mailing_address}</div>}
                                  {r.is_student && <div className="mt-1"><Badge variant="secondary">Student{r.university ? ` · ${r.university}` : ""}</Badge></div>}
                                </div>
                                <div>
                                  <div className="font-semibold text-sm mb-1">Family Members ({memberList.length})</div>
                                  {memberList.length === 0 ? (
                                    <div className="text-muted-foreground">No additional members.</div>
                                  ) : (
                                    <ul className="space-y-1">
                                      {memberList.map(m => (
                                        <li key={m.id} className="flex justify-between gap-2">
                                          <span>{m.first_name} {m.middle_name || ""} {m.last_name}</span>
                                          <span className="text-muted-foreground">{m.relationship.replace(/_/g, " ")} · {ageLabel(m.age_group)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="font-semibold text-sm mb-1">Payment & Accommodation</div>
                                  <div>Category: <span className="capitalize">{r.category || "—"}</span></div>
                                  <div>Fee: <span className="font-mono">${Number(r.category_price ?? 0).toFixed(0)}</span> · Paid: <span className="font-mono">${Number(r.amount_paid ?? 0).toFixed(0)}</span> · Due: <span className="font-mono">${Number(r.amount_due ?? Math.max(Number(r.category_price ?? 0) - Number(r.amount_paid ?? 0), 0)).toFixed(0)}</span></div>
                                  <div>Method: {methodLabel[r.payment_choice || ""] || r.payment_choice || "—"}</div>
                                  <div>Status: {isPaid ? "Paid" : "Not Paid"}</div>
                                  {r.transaction_reference && <div>Txn: <span className="font-mono">{r.transaction_reference}</span></div>}
                                  {r.zelle_memo && <div>Memo: <span className="font-mono">{r.zelle_memo}</span></div>}
                                  {r.boarding_type && <div className="mt-1">Accommodation: <span className="capitalize">{r.boarding_type.replace(/_/g, " ")}</span></div>}
                                  {r.dietary_notes && <div className="mt-1"><strong>Dietary/Medical:</strong> {r.dietary_notes}</div>}
                                  {r.general_comments && <div className="mt-1"><strong>Comments:</strong> {r.general_comments}</div>}
                                  {r.admin_notes && <div className="mt-1"><strong>Admin notes:</strong> {r.admin_notes}</div>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>

                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Rows per page</span>
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[80px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50, 100].map(n => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage(1)} disabled={page === 1} aria-label="First page"><ChevronsLeft className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} aria-label="Previous page"><ChevronLeft className="w-4 h-4" /></Button>
                <span className="px-3 text-muted-foreground">Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong></span>
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page"><ChevronRight className="w-4 h-4" /></Button>
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setPage(totalPages)} disabled={page >= totalPages} aria-label="Last page"><ChevronsRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>



      {/* Mark Paid / Unpaid dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update payment status</DialogTitle>
            <DialogDescription>
              {editing && (
                <>
                  <strong>{editing.first_name} {editing.last_name}</strong> · {editing.region} · {editing.email}
                  <br />
                  <span className="text-xs">Zelle memo: <span className="font-mono">{editing.zelle_memo}</span></span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="txn-ref">Bank / Zelle transaction reference</Label>
              <Input
                id="txn-ref"
                value={txnRef}
                onChange={(e) => setTxnRef(e.target.value)}
                placeholder="e.g. Zelle confirmation #ABC123 or check #1042"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Helps match this registration against the bank confirmation message.
              </p>
            </div>
            <div>
              <Label htmlFor="admin-notes">Notes (visible to admins only)</Label>
              <Textarea
                id="admin-notes"
                rows={3}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="e.g. partial payment of $125; remainder due at desk"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" disabled={saving} onClick={() => savePayment("pending")}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Circle className="w-4 h-4 mr-2" />}
              Mark Unpaid
            </Button>
            <Button
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => savePayment("confirmed")}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Mark Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Payment status history</DialogTitle>
            <DialogDescription>
              {historyFor && (<>{historyFor.first_name} {historyFor.last_name} · {historyFor.region}</>)}
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No changes recorded yet.</p>
          ) : (
            <div className="max-h-96 overflow-auto divide-y">
              {history.map((h) => (
                <div key={h.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="text-xs uppercase text-muted-foreground">
                        {h.from_status === "confirmed" ? "Paid" : h.from_status ? "Not Paid" : "—"}
                      </span>
                      <span className="mx-2">→</span>
                      <span className={`font-semibold ${h.to_status === "confirmed" ? "text-emerald-700" : "text-amber-700"}`}>
                        {h.to_status === "confirmed" ? "Paid" : "Not Paid"}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(h.changed_at).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    by {h.changed_by_name || h.changed_by_email || "—"}
                  </div>
                  {h.transaction_reference && (
                    <div className="text-xs mt-1"><strong>Txn ref:</strong> <span className="font-mono">{h.transaction_reference}</span></div>
                  )}
                  {h.notes && <div className="text-xs mt-1 text-slate-700">{h.notes}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && !deleteBusy && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this registration?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This permanently removes the registration record
                  {deleting?.payment_screenshot_path ? " and its uploaded payment screenshot" : ""}.
                  This cannot be undone.
                </p>
                {deleting && (
                  <div className="rounded-md border bg-muted/50 p-3 text-xs space-y-1">
                    <div><strong>{deleting.first_name} {deleting.last_name}</strong> — {deleting.email}</div>
                    <div className="text-muted-foreground">{deleting.region}{deleting.city ? `, ${deleting.city}` : ""} · {deleting.total_members} attendees</div>
                    <div className="font-mono text-muted-foreground">Reg ID: {deleting.id}</div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteBusy}
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default ConferenceRegistrationsManager;
